import React, { useState, useEffect, lazy, Suspense } from 'react';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useParams, Navigate, useNavigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "./contexts/LanguageContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SiteOfficeProvider, useSiteOffice } from "./contexts/SiteOfficeContext";
import { ProjectProvider } from "./contexts/ProjectContext";
import { ScheduleProvider } from "./contexts/ScheduleContext";
import { UserRoleProvider } from './contexts/UserRoleContext';
import { YearProvider, useYear } from './contexts/YearContext';
import { LoginThemeProvider } from './contexts/LoginThemeContext';
import { SidebarProvider } from "@/components/ui/sidebar";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Loading from "./components/Loading";
import { LoginPage } from '@/components/auth/LoginPage';
import { AdminSetup } from '@/components/admin/AdminSetup';
const AlwaysOnBottomChat = lazyWithRetry(() => import('@/components/chat/AlwaysOnBottomChat').then(m => ({ default: m.AlwaysOnBottomChat })));
const ScreenshotCaptureManager = lazyWithRetry(() => import('@/components/info/ScreenshotCaptureManager'));
import { useAppSettings } from '@/hooks/useAppSettings';
import { autoDeleteService } from './services/autoDeleteService';
import AppVersion from '@/components/AppVersion';
// Import debug script for team status debugging
import './debug-team-status';

// Lazy-loaded admin components
const ChatManagement = lazyWithRetry(() => import('@/components/admin/ChatManagement').then(m => ({ default: m.ChatManagement })));
const MemberManagement = lazyWithRetry(() => import('@/components/admin/MemberManagement').then(m => ({ default: m.MemberManagement })));
const AdminDashboard = lazyWithRetry(() => import('@/components/admin/AdminDashboard'));
const UserActivityPage = lazyWithRetry(() => import('@/components/admin/UserActivityPage'));
const MaintenanceModePage = lazyWithRetry(() => import('@/components/admin/MaintenanceModePage'));
const SystemLogsPage = lazyWithRetry(() => import('@/components/admin/SystemLogsPage'));
const AppConfigPage = lazyWithRetry(() => import('@/components/admin/AppConfigPage'));
const DatabaseManagementPage = lazyWithRetry(() => import('@/components/admin/DatabaseManagementPage'));
const ChatbotManagementPage = lazyWithRetry(() => import('@/components/admin/ChatbotManagementPage'));
const WorkGroupsManagementPage = lazyWithRetry(() => import('@/components/admin/WorkGroupsManagementPage'));
const BackupRestorePage = lazyWithRetry(() => import('@/components/admin/BackupRestorePage'));
const EmailReportSchedulesPage = lazyWithRetry(() => import('@/components/admin/EmailReportSchedulesPage'));
const OwnerShareLinksPage = lazyWithRetry(() => import('@/components/admin/OwnerShareLinksPage'));
const OwnerSharePage = lazyWithRetry(() => import('./pages/OwnerSharePage'));
const OwnerAuthPage = lazyWithRetry(() => import('./pages/OwnerAuthPage'));

// Lazy-loaded page components with preload support
const importProjectsPage = () => import("./pages/ProjectsPage");
const importSchedulePage = () => import("./pages/SchedulePage");
const importDocumentsPage = () => import("./pages/DocumentsPage");
const importDashboardPage = () => import("./pages/DashboardPage");
const importTeamsPage = () => import("./pages/TeamsPage");
const importReportsPage = () => import("./pages/ReportsPage");
const importBudgetRABPage = () => import("./pages/budget/BudgetRABPage");
const importSettingsPage = () => import("./pages/SettingsPage");

const ProjectsPage = lazyWithRetry(importProjectsPage);
const SchedulePage = lazyWithRetry(importSchedulePage);
const DocumentsPage = lazyWithRetry(importDocumentsPage);
const DashboardPage = lazyWithRetry(importDashboardPage);
const InfoPage = lazyWithRetry(() => import("./pages/InfoPage"));
const DatabaseViewPage = lazyWithRetry(() => import("./pages/DatabaseViewPage"));
const TeamsPage = lazyWithRetry(importTeamsPage);
const ReportsPage = lazyWithRetry(importReportsPage);
const BudgetRABPage = lazyWithRetry(importBudgetRABPage);
const SafetyPage = lazyWithRetry(() => import("./pages/SafetyPage"));
const MarketingTenderPage = lazyWithRetry(() => import("./pages/MarketingTenderPage"));
const WhGaPage = lazyWithRetry(() => import("./pages/WhGaPage"));
const SettingsPage = lazyWithRetry(importSettingsPage);
const ActivityLogsPage = lazyWithRetry(() => import("./pages/ActivityLogsPage"));
const AnnouncementsPage = lazyWithRetry(() => import("./pages/AnnouncementsPage"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Register = lazyWithRetry(() => import("./pages/Register"));
const VerifyEmail = lazyWithRetry(() => import("./pages/VerifyEmail"));
const AnnualPlanningPage = lazyWithRetry(() => import("./pages/AnnualPlanningPage"));
const MachineShutdownPage = lazyWithRetry(() => import("./pages/MachineShutdownPage"));

// Lazy-loaded schedule components
const ScheduleInput = lazyWithRetry(() => import("./pages/schedule/ScheduleInput").then(m => ({ default: m.ScheduleInput })));
const ProjectScheduleDetail = lazyWithRetry(() => import("./components/schedule/ProjectScheduleDetail"));
const GanttChartDetail = lazyWithRetry(() => import("./components/schedule/GanttChartDetail").then(m => ({ default: m.GanttChartDetail })));

// Preload core pages after auth to eliminate loading spinners on navigation
const preloadCorePages = () => {
  // Use requestIdleCallback (or setTimeout fallback) to avoid blocking the main thread
  const schedule = window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 100));
  schedule(() => {
    importDashboardPage();
    importProjectsPage();
    importTeamsPage();
    importSchedulePage();
    importBudgetRABPage();
    importDocumentsPage();
    importReportsPage();
    importSettingsPage();
  });
};

// Kick off preloads as soon as the module evaluates so navigation feels instant
// (chunks finish loading before the user clicks the menu)
if (typeof window !== 'undefined') {
  const schedule = window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 50));
  schedule(() => {
    importDashboardPage();
    importProjectsPage();
    importTeamsPage();
    importSchedulePage();
    importBudgetRABPage();
    importDocumentsPage();
    importReportsPage();
    importSettingsPage();
  });
}

// Other imports
import { MaintenancePage } from '@/components/MaintenancePage';
import SiteOfficeSelection from './components/SiteOfficeSelection';

const queryClient = new QueryClient();

const GanttChartWrapper = () => {
  const { projectId } = useParams();
  return (
    <GanttChartDetail 
      project={{
        id: projectId || '',
        name: '',
        projectNumber: '',
        pic: '',
        location: '',
        startDate: new Date().toISOString(),
        endDate: new Date().toISOString(),
        workStatus: 'In Progress',
        status: 'In Progress',
        progress: 0,
        materials: [],
        labor: [],
        supervisor: '',
        description: '',
        createdAt: new Date(),
        updatedAt: new Date()
      }}
      onUpdate={() => {}}
    />
  );
};

// Add AdminRoute component for protected routes
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { teamMember, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait until auth/team data finishes loading before deciding access
    if (isLoading || !teamMember) return;

    // Check if user is not admin
    if (!teamMember?.is_admin && teamMember?.role !== 'administrator') {
      console.log('❌ Access denied:', {
        is_admin: teamMember?.is_admin,
        role: teamMember?.role
      });
      navigate('/dashboard');
      return;
    }

    console.log('✅ Admin access granted:', {
      is_admin: teamMember?.is_admin,
      role: teamMember?.role
    });
  }, [teamMember, isLoading, navigate]);

  // Show loading state while we don't yet know the role
  if (isLoading || !teamMember) {
    return <Loading />;
  }

  // Only render children if user is admin
  if (!teamMember?.is_admin && teamMember?.role !== 'administrator') {
    return null;
  }

  return <>{children}</>;
};

const MainAppContent = () => {
  const { selectedSiteOffice, setSiteOffice } = useSiteOffice();
  const { selectedYear } = useYear();
  const { isAuthenticated, isLoading, teamMember } = useAuth();
  const { settings: appSettings, loading: settingsLoading } = useAppSettings();
  const [appLoading, setAppLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const initApp = async () => {
      try {
        // Initialize the app without Supabase connection test
        console.log('Initializing Progress Compass application...');
        
        // Check if we're in setup mode (for initial admin setup)
        const isSetupMode = localStorage.getItem('setupMode') === 'true';
        setSetupMode(isSetupMode);
        
      } catch (err) {
        console.error('Error in app initialization:', err);
      } finally {
        setTimeout(() => {
          setAppLoading(false);
        }, 500);
      }
    };

    initApp();
  }, []);

  useEffect(() => {
    // Start auto-delete service
    autoDeleteService.startAutoDeleteJob();

    // Clean up on unmount
    return () => {
      autoDeleteService.stopAutoDeleteJob();
    };
  }, []);

  // Preload core pages once authenticated to eliminate navigation loading spinners
  useEffect(() => {
    if (isAuthenticated && !isLoading && selectedSiteOffice) {
      preloadCorePages();
    }
  }, [isAuthenticated, isLoading, selectedSiteOffice]);

  // Check if we're on the published domain (not preview)
  const isPublishedDomain = typeof window !== 'undefined' && (
    window.location.hostname === 'gcwproject.app' ||
    window.location.hostname === 'www.gcwproject.app'
  );

  // Check if user is admin
  const isAdmin = teamMember?.is_admin || teamMember?.role === 'administrator';

  // Show maintenance page if:
  // 1. Maintenance mode is enabled
  // 2. We're on the published domain (not preview)
  // 3. User is NOT an admin (admins can still access)
  // 4. Settings have finished loading
  if (!settingsLoading && appSettings.maintenance_mode && appSettings.maintenance_level === 'heavy' && isPublishedDomain && !isAdmin && isAuthenticated) {
    return (
      <MaintenancePage 
        title={appSettings.maintenance_title}
        message={appSettings.maintenance_message}
      />
    );
  }

  // Show loading screen while checking auth or app initialization
  if (isLoading || appLoading) {
    return <Loading />;
  }
  
  // Show login page if not authenticated and not in setup mode
  // But allow access to register page
  if (!isAuthenticated && !setupMode) {
    // Check maintenance mode for unauthenticated users on published domain
    if (!settingsLoading && appSettings.maintenance_mode && appSettings.maintenance_level === 'heavy' && isPublishedDomain) {
      return (
        <MaintenancePage 
          title={appSettings.maintenance_title}
          message={appSettings.maintenance_message}
        />
      );
    }
    
    return (
      <div className="relative">
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="*" element={<LoginPage />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  // Show setup mode banner if in setup mode
  const SetupBanner = () => (
    <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center py-3 text-sm z-50 shadow-lg">
      <div className="flex items-center justify-center gap-4">
        <span className="font-bold">🔧 SETUP MODE ACTIVE</span>
        <span>|</span>
        <span>👑 Administrator Privileges Enabled</span>
        <span>|</span>
        <span>🔓 Authentication Bypassed</span>
        <button
          onClick={() => {
            localStorage.removeItem('setupMode');
            setSetupMode(false);
            window.location.reload(); // Force reload to clear setup state
          }}
          className="ml-4 bg-amber-600 px-3 py-1 rounded text-xs hover:bg-amber-700 font-medium"
        >
          Exit Setup Mode
        </button>
      </div>
      <div className="text-xs mt-1 opacity-90">
        All menus visible • All buttons active • Can add team members
      </div>
    </div>
  );

  // Show site office selection if no office is selected
  if (!selectedSiteOffice) {
    return (
      <div className="relative min-h-screen min-h-svh">
        {setupMode && <SetupBanner />}
        <div className={setupMode ? 'pt-20' : ''}>
          <SiteOfficeSelection onSiteOfficeSelected={(office) => {
            setSiteOffice(office);
            navigate('/dashboard');
          }} />
        </div>
      </div>
    );
  }


  return (
    <div className="relative">
      {setupMode && <SetupBanner />}
      <div className={setupMode ? 'pt-20' : ''}>
        <ProjectProvider key={`${selectedSiteOffice}-${selectedYear}`}>
          <ScheduleProvider>
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/announcements" element={<AnnouncementsPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/:projectId" element={<ProjectsPage />} />
                <Route path="/teams" element={<TeamsPage />} />
                <Route path="/activity-logs/:projectId" element={<ActivityLogsPage />} />
                <Route path="/schedule" element={<SchedulePage />} />
                <Route path="/schedule-stop-mesin" element={<MachineShutdownPage />} />
                <Route path="/planning" element={<AnnualPlanningPage />} />
                <Route path="/schedule/planning" element={<Navigate to="/planning" replace />} />
                <Route path="/schedule/input/:projectId" element={<ScheduleInput />} />
                <Route path="/schedule/gantt/:projectId" element={<GanttChartWrapper />} />
                <Route path="/schedule/:projectId" element={<ProjectScheduleDetail />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route path="/documents/:projectId" element={<DocumentsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/reports/:reportType" element={<ReportsPage />} />
                <Route path="/reports/:projectId" element={<ReportsPage />} />
                <Route path="/budget" element={<BudgetRABPage />} />
                <Route path="/budget/:projectId" element={<BudgetRABPage />} />
                <Route path="/safety" element={<SafetyPage />} />
                <Route path="/marketing" element={<MarketingTenderPage />} />
                <Route path="/wh-ga" element={<WhGaPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/info" element={<InfoPage />} />
                <Route path="/database" element={<DatabaseViewPage />} />
                
                <Route path="/admin-setup" element={<AdminSetup />} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="/admin/activity" element={<AdminRoute><UserActivityPage /></AdminRoute>} />
                <Route path="/admin/chat" element={<AdminRoute><ChatManagement /></AdminRoute>} />
                <Route path="/admin/members" element={<AdminRoute><MemberManagement /></AdminRoute>} />
                <Route path="/admin/maintenance" element={<AdminRoute><MaintenanceModePage /></AdminRoute>} />
                <Route path="/admin/chatbot" element={<AdminRoute><ChatbotManagementPage /></AdminRoute>} />
                
                <Route path="/admin/logs" element={<AdminRoute><SystemLogsPage /></AdminRoute>} />
                <Route path="/admin/config" element={<AdminRoute><AppConfigPage /></AdminRoute>} />
                
                <Route path="/admin/database" element={<AdminRoute><DatabaseManagementPage /></AdminRoute>} />
                <Route path="/admin/work-groups" element={<AdminRoute><WorkGroupsManagementPage /></AdminRoute>} />
                <Route path="/admin/backup" element={<AdminRoute><BackupRestorePage /></AdminRoute>} />
                <Route path="/admin/email-reports" element={<AdminRoute><EmailReportSchedulesPage /></AdminRoute>} />
                <Route path="/admin/owner-links" element={<AdminRoute><OwnerShareLinksPage /></AdminRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <div className="text-center text-[10px] text-muted-foreground py-2 my-4 print:hidden">
                <AppVersion />
              </div>
            </Suspense>
            <AlwaysOnBottomChat />
            <Suspense fallback={null}><ScreenshotCaptureManager /></Suspense>
          </ScheduleProvider>
        </ProjectProvider>
      </div>
    </div>
  );
};
const App = () => {
  useEffect(() => {
    // Start auto-delete service
    autoDeleteService.startAutoDeleteJob();

    // Clean up on unmount
    return () => {
      autoDeleteService.stopAutoDeleteJob();
    };
  }, []);

    return (
    <div className="app-container">
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <SettingsProvider>
                <LanguageProvider>
                  <LoginThemeProvider>
                    <BrowserRouter>
                      <Routes>
                        {/* Owner portal (login khusus owner, view only) */}
                        <Route
                          path="/owner"
                          element={
                            <Suspense fallback={<Loading />}>
                              <OwnerAuthPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/owner/dashboard"
                          element={
                            <Suspense fallback={<Loading />}>
                              <OwnerSharePage accountMode />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/owner/view"
                          element={
                            <Suspense fallback={<Loading />}>
                              <OwnerSharePage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/owner/:token"
                          element={
                            <Suspense fallback={<Loading />}>
                              <OwnerSharePage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="*"
                          element={
                            <SiteOfficeProvider>
                              <YearProvider>
                                <AuthProvider>
                                  <UserRoleProvider>
                                    <SidebarProvider>
                                      <MainAppContent />
                                    </SidebarProvider>
                                  </UserRoleProvider>
                                </AuthProvider>
                              </YearProvider>
                            </SiteOfficeProvider>
                          }
                        />
                      </Routes>
                    </BrowserRouter>
                  </LoginThemeProvider>
                </LanguageProvider>
              </SettingsProvider>
            </TooltipProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </div>
  );
};

export default App;
